import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ScreeningRoomLayout from './components/ScreeningRoomLayout';
import PrivateRoute from './components/PrivateRoute';
import ScreeningRoomLogin from './pages/ScreeningRoomLogin';
import Register from './pages/Register';
import websocketService from './services/websocket';

// Pages
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';

// Encode
import Jobs from './pages/Jobs';
import CreateJob from './pages/CreateJob';
import DCPJobs from './pages/DCPJobs';
import CreateDCPJob from './pages/CreateDCPJob';
import DCPDistribute from './pages/DCPDistribute';

// Distribute
import Library from './pages/Library';
import DCPDistribution from './pages/DCPDistribution';
import BookingCalendar from './pages/BookingCalendar';
import Downloads from './pages/Downloads';

// Infrastructure
import Nodes from './pages/Nodes';
import VenueCertificates from './pages/VenueCertificates';
import Campaigns from './pages/Campaigns';
import CampaignStatistics from './pages/CampaignStatistics';
import KeyArt from './pages/KeyArt';

// Landing
import LandingPage from './pages/LandingPage';
import InviteLanding from './pages/InviteLanding';
import CompanyPicker from './pages/CompanyPicker';
import RoleGuard from './components/RoleGuard';
import CompanyContextGuard from './components/CompanyContextGuard';
import SuperadminGuard from './components/SuperadminGuard';

const FULL = ['admin', 'user'];        // not venue_manager
const ALL = ['admin', 'user', 'venue_manager'];

// Settings
import UserAdmin from './pages/UserAdmin';
import CompanySettings from './pages/CompanySettings';
import CompanyTeam from './pages/CompanyTeam';
import Sources from './pages/Sources';
import { UploadManagerProvider } from './contexts/UploadManagerContext';
import EncodingProfiles from './pages/EncodingProfiles';
import TemplateBuilder from './pages/TemplateBuilder';
import AuditLog from './pages/AuditLog';
import Templates from './pages/Templates';
import StorageSettings from './pages/StorageSettings';
import EmailConfig from './pages/EmailConfig';
import SystemSettings from './pages/SystemSettings';

// Operator
import OperatorCompanies from './pages/operator/OperatorCompanies';
import SiteAdmins from './pages/SiteAdmins';
import FleetHealth from './pages/operator/FleetHealth';
import BugReports from './pages/operator/BugReports';
import PhysicalDrives from './pages/operator/PhysicalDrives';

function App() {
  const { userToken } = useSelector((state) => state.auth);

  useEffect(() => {
    if (userToken) {
      websocketService.connect();
      websocketService.subscribe('*');
    } else {
      websocketService.disconnect();
    }
    return () => { websocketService.disconnect(); };
  }, [userToken]);

  return (
    <UploadManagerProvider>
    <Routes>
      {/* Public */}
      <Route path="/login" element={!userToken ? <ScreeningRoomLogin /> : <Navigate to="/" />} />
      <Route path="/register" element={!userToken ? <Register /> : <Navigate to="/" />} />
      <Route path="/welcome" element={!userToken ? <LandingPage /> : <Navigate to="/" />} />
      <Route path="/invite/:token" element={<InviteLanding />} />

      {/* Company picker (after login) */}
      <Route path="/pick-company" element={<PrivateRoute><CompanyPicker /></PrivateRoute>} />

      {/* Protected — wraps in CompanyContextGuard so all routes get tenant context */}
      <Route path="/" element={<PrivateRoute><CompanyContextGuard><ScreeningRoomLayout /></CompanyContextGuard></PrivateRoute>}>
        <Route index element={<RoleGuard allow={FULL}><Dashboard /></RoleGuard>} />
        <Route path="profile" element={<Profile />} />

        {/* Encode (full users only) */}
        <Route path="screeners" element={<RoleGuard allow={FULL}><Jobs /></RoleGuard>} />
        <Route path="screeners/create" element={<RoleGuard allow={FULL}><CreateJob /></RoleGuard>} />
        <Route path="dcp" element={<RoleGuard allow={FULL}><DCPJobs /></RoleGuard>} />
        <Route path="dcp/create" element={<RoleGuard allow={FULL}><CreateDCPJob /></RoleGuard>} />
        <Route path="dcp/:jobId/distribute" element={<RoleGuard allow={ALL}><DCPDistribute /></RoleGuard>} />

        {/* Distribute */}
        <Route path="library" element={<RoleGuard allow={ALL}><Library /></RoleGuard>} />
        <Route path="sources" element={<RoleGuard allow={FULL}><Sources /></RoleGuard>} />
        <Route path="distribution" element={<RoleGuard allow={ALL}><DCPDistribution /></RoleGuard>} />
        {/* Legacy: /kdms is folded into /distribution as a tab. */}
        <Route path="kdms" element={<Navigate to="/distribution?tab=kdms" replace />} />
        <Route path="calendar" element={<RoleGuard allow={ALL}><BookingCalendar /></RoleGuard>} />
        <Route path="downloads" element={<RoleGuard allow={FULL}><Downloads /></RoleGuard>} />

        {/* Infrastructure */}
        <Route path="venues" element={<RoleGuard allow={ALL}><VenueCertificates /></RoleGuard>} />
        <Route path="campaigns" element={<RoleGuard allow={FULL}><Campaigns /></RoleGuard>} />
        <Route path="statistics/:campaignId" element={<RoleGuard allow={FULL}><CampaignStatistics /></RoleGuard>} />
        <Route path="key-art" element={<RoleGuard allow={FULL}><KeyArt /></RoleGuard>} />

        {/* Settings (admin only) */}
        <Route path="settings/users" element={<RoleGuard allow={FULL}><UserAdmin /></RoleGuard>} />
        <Route path="settings/workspace" element={<RoleGuard allow={FULL}><CompanySettings /></RoleGuard>} />
        <Route path="settings/team" element={<RoleGuard allow={FULL}><CompanyTeam /></RoleGuard>} />
        <Route path="settings/profiles" element={<RoleGuard allow={FULL}><EncodingProfiles /></RoleGuard>} />
        <Route path="settings/templates" element={<RoleGuard allow={FULL}><Templates /></RoleGuard>} />
        <Route path="templates/create" element={<RoleGuard allow={FULL}><TemplateBuilder /></RoleGuard>} />
        <Route path="templates/edit/:id" element={<RoleGuard allow={FULL}><TemplateBuilder /></RoleGuard>} />
        <Route path="settings/audit" element={<RoleGuard allow={FULL}><AuditLog /></RoleGuard>} />

        {/* Operator (HHMG super-admin only) */}
        <Route path="operator" element={<SuperadminGuard><Navigate to="/operator/companies" replace /></SuperadminGuard>} />
        <Route path="operator/companies" element={<SuperadminGuard><OperatorCompanies /></SuperadminGuard>} />
        <Route path="operator/site-admins" element={<SuperadminGuard><SiteAdmins /></SuperadminGuard>} />
        <Route path="operator/fleet-health" element={<SuperadminGuard><FleetHealth /></SuperadminGuard>} />
        <Route path="operator/bug-reports" element={<SuperadminGuard><BugReports /></SuperadminGuard>} />
        <Route path="operator/nodes" element={<SuperadminGuard><Nodes /></SuperadminGuard>} />
        <Route path="operator/nodes/:variant" element={<SuperadminGuard><Nodes /></SuperadminGuard>} />
        <Route path="operator/storage" element={<SuperadminGuard><StorageSettings /></SuperadminGuard>} />
        <Route path="operator/email" element={<SuperadminGuard><EmailConfig /></SuperadminGuard>} />
        <Route path="operator/system" element={<SuperadminGuard><SystemSettings /></SuperadminGuard>} />
        <Route path="operator/audit" element={<SuperadminGuard><AuditLog /></SuperadminGuard>} />
        <Route path="operator/physical-drives" element={<SuperadminGuard><PhysicalDrives /></SuperadminGuard>} />

        {/* Legacy redirects */}
        <Route path="queue" element={<Navigate to="/screeners" replace />} />
        <Route path="queue/create" element={<Navigate to="/screeners/create" replace />} />
        <Route path="encoders" element={<Navigate to="/operator/nodes" replace />} />
        {/* (former) /distribution → /library redirect REMOVED in v9.22.0;
            /distribution is now its own page. */}
        <Route path="admin" element={<Navigate to="/operator/system" replace />} />
        <Route path="admin/*" element={<Navigate to="/operator/system" replace />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </UploadManagerProvider>
  );
}

export default App;
